import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleDollarSign, Euro, FolderKanban, Plus, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, eur, errMsg, fmtDate, PAY_METHODS, STATUS } from "../lib/api";
import { EmptyState, PageHeader, PayBadge } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PaymentForm } from "../components/SimpleForms";

function rangeFor(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (key === "last_month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (key === "year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: "", to: "" };
}

export default function Payments() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [range, setRange] = useState("month");
  const [formOpen, setFormOpen] = useState(false);
  const [defaultPid, setDefaultPid] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { from, to } = rangeFor(range);
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/payments/overview", { params });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } catch {}
    })();
  }, []);

  const cards = data
    ? [
        { l: "Offen an Mondher", v: eur(data.total_open), icon: CircleDollarSign, highlight: true, testid: "pay-stat-open" },
        { l: "Bereits bezahlt (gesamt)", v: eur(data.total_paid), icon: Wallet, testid: "pay-stat-paid" },
        { l: "Bezahlt im Zeitraum", v: eur(data.paid_in_range), icon: TrendingUp, testid: "pay-stat-range" },
        { l: "Projektwert gesamt", v: eur(data.total_value), icon: Euro, testid: "pay-stat-value" },
        { l: "Projekte mit offenem Betrag", v: data.open_projects, icon: FolderKanban, testid: "pay-stat-open-projects" },
      ]
    : [];

  return (
    <div className="animate-fade-up" data-testid="payments-page">
      <PageHeader overline="Finanzen" title="Zahlungen an Mondher" testid="payments-header">
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger data-testid="payments-range" className="h-9 w-44 rounded-xl bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Dieser Monat</SelectItem>
            <SelectItem value="last_month">Letzter Monat</SelectItem>
            <SelectItem value="year">Dieses Jahr</SelectItem>
            <SelectItem value="all">Gesamt</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="new-payment-button" onClick={() => { setDefaultPid(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Zahlung eintragen
        </Button>
      </PageHeader>

      {!data ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">{[...Array(5)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-white" />)}</div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
            {cards.map((c) => (
              <div
                key={c.l}
                data-testid={c.testid}
                className={`rounded-2xl border p-4 shadow-sm ${c.highlight ? "border-[#B88A4A]/40 bg-[#B88A4A]/5" : "border-[#1D225C]/10 bg-white"}`}
              >
                <c.icon className="mb-2 h-5 w-5 text-[#B88A4A]" />
                <p className={`font-display text-xl font-bold ${c.highlight ? "text-[#B88A4A]" : "text-[#1D225C]"}`}>{c.v}</p>
                <p className="mt-0.5 text-xs font-medium text-[#777]">{c.l}</p>
              </div>
            ))}
          </div>

          <section className="mb-6" data-testid="payments-projects-section">
            <h3 className="mb-3 font-display text-base font-semibold text-[#1D225C]">Pro Projekt</h3>
            {data.rows.length === 0 ? (
              <EmptyState icon={Euro} title="Noch keine Projekte mit Finanzdaten" hint="Erfasse Preis und Mondher-Honorar im Projekt." testid="payments-empty" />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                <table className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className="border-b border-[#1D225C]/10 text-left text-xs uppercase tracking-wide text-[#777]">
                      <th className="px-4 py-3 font-semibold">Projekt</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Kundenpreis</th>
                      <th className="px-4 py-3 text-right font-semibold">Honorar</th>
                      <th className="px-4 py-3 text-right font-semibold">Bezahlt</th>
                      <th className="px-4 py-3 text-right font-semibold">Offen</th>
                      <th className="px-4 py-3 font-semibold">Zahlung</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.project_id} data-testid={`payment-project-row-${r.project_id}`} className="border-b border-[#1D225C]/5 last:border-0 hover:bg-[#F7F6F2]">
                        <td className="cursor-pointer px-4 py-3" onClick={() => navigate(`/projekte/${r.project_id}?tab=zahlungen`)}>
                          <p className="font-semibold text-[#1D225C]">{r.project_name}</p>
                          <p className="text-xs text-[#777]">{r.client_name}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#777]">{STATUS[r.status]?.label}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1D225C]">{eur(r.price)}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1D225C]">{eur(r.agreed_payment)}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#22C55E]">{eur(r.paid)}</td>
                        <td className="px-4 py-3 text-right font-display font-bold text-[#B88A4A]">{eur(r.open)}</td>
                        <td className="px-4 py-3"><PayBadge status={r.payment_status} /></td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`payment-add-for-${r.project_id}`}
                            onClick={() => { setDefaultPid(r.project_id); setFormOpen(true); }}
                            className="rounded-lg text-xs"
                          >
                            + Zahlung
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section data-testid="payments-history-section">
            <h3 className="mb-3 font-display text-base font-semibold text-[#1D225C]">Zahlungshistorie</h3>
            {data.history.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-[#1D225C]/15 bg-white/60 px-4 py-8 text-center text-sm text-[#777]">
                Keine Zahlungen im gewählten Zeitraum.
              </p>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                {data.history.map((p, i) => (
                  <div key={p.id} data-testid={`payment-history-${p.id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                    <span className="font-display text-base font-bold text-[#1D225C]">{eur(p.amount)}</span>
                    <span className="text-xs text-[#777]">{fmtDate(p.date)}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-[#1D225C]">{p.client_name} · {p.project_name}</span>
                    <span className="rounded-lg bg-[#1D225C]/5 px-2 py-0.5 text-[11px] font-medium text-[#1D225C]">{PAY_METHODS[p.method] || p.method}</span>
                    {p.notes && <span className="text-xs text-[#999]">{p.notes}</span>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <PaymentForm open={formOpen} onOpenChange={setFormOpen} projects={projects} defaultProjectId={defaultPid} onSaved={load} />
    </div>
  );
}
